import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { 
  ArrowLeft, 
  Upload, 
  X, 
  Check, 
  AlertCircle,
  Image as ImageIcon,
  Loader2,
  CheckCircle2,
  Info
} from "lucide-react";

const IMAGE_TYPES = [
  { 
    id: "front", 
    label: "Front View", 
    required: true, 
    description: "Main product view (Required)",
    icon: "📷"
  },
  { 
    id: "back", 
    label: "Back View", 
    required: false, 
    description: "Rear view of the product",
    icon: "🔄"
  },
  { 
    id: "side", 
    label: "Side View", 
    required: false, 
    description: "Profile/side angle",
    icon: "↔️"
  },
  { 
    id: "zoom", 
    label: "Zoom / Detail", 
    required: false, 
    description: "Close-up of fabric/details",
    icon: "🔍"
  }
];

const AddProduct = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  
  // Form state
  const [skuCode, setSkuCode] = useState("");
  const [skuName, setSkuName] = useState("");
  const [category, setCategory] = useState("fashion");
  
  // Images state
  const [images, setImages] = useState({
    front: { file: null, url: "", uploading: false, error: null },
    back: { file: null, url: "", uploading: false, error: null },
    side: { file: null, url: "", uploading: false, error: null },
    zoom: { file: null, url: "", uploading: false, error: null }
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`, { withCredentials: true });
      setCategories(response.data);
    } catch (error) {
      console.error("Failed to load categories");
    }
  };

  const handleImageSelect = async (type, file) => {
    if (!file) return;
    
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }
    
    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image must be less than 10MB");
      return;
    }
    
    // Create preview
    const previewUrl = URL.createObjectURL(file);
    setImages(prev => ({
      ...prev,
      [type]: { ...prev[type], file, previewUrl, uploading: true, error: null }
    }));
    
    // Upload file
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await axios.post(`${API}/upload`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      setImages(prev => ({
        ...prev,
        [type]: { ...prev[type], url: response.data.url, uploading: false }
      }));
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} image uploaded`);
    } catch (error) {
      setImages(prev => ({
        ...prev,
        [type]: { ...prev[type], uploading: false, error: "Upload failed" }
      }));
      toast.error(`Failed to upload ${type} image`);
    }
  };

  const handleRemoveImage = (type) => {
    if (images[type].previewUrl) {
      URL.revokeObjectURL(images[type].previewUrl);
    }
    setImages(prev => ({
      ...prev,
      [type]: { file: null, url: "", uploading: false, error: null }
    }));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (type) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files[0];
    if (file) {
      handleImageSelect(type, file);
    }
  };

  const canSubmit = () => {
    return skuCode.trim() && skuName.trim() && images.front.url;
  };

  const handleSubmit = async () => {
    if (!canSubmit()) {
      if (!skuCode.trim()) toast.error("Please enter a SKU code");
      else if (!skuName.trim()) toast.error("Please enter a product name");
      else if (!images.front.url) toast.error("Front image is required");
      return;
    }
    
    setSaving(true);
    try {
      // Step 1: Create SKU
      const skuResponse = await axios.post(`${API}/skus`, {
        sku_code: skuCode,
        sku_name: skuName,
        category: category
      }, { withCredentials: true });
      
      const newSkuId = skuResponse.data.sku_id;
      
      // Step 2: Upload images to SKU
      const imageData = {};
      Object.entries(images).forEach(([type, data]) => {
        if (data.url) {
          imageData[type] = data.url;
        }
      });
      
      await axios.post(`${API}/skus/${newSkuId}/images`, {
        images: imageData
      }, { withCredentials: true });
      
      toast.success("Product created successfully!");
      navigate(`/products/${newSkuId}`);
      
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to create product";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const uploadedCount = Object.values(images).filter(img => img.url).length;

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/products")}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Add New Product</h1>
            <p className="text-gray-600">Upload multiple images for professional AI photoshoots</p>
          </div>
        </div>

        {/* Product Info Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Product Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sku-code">SKU Code *</Label>
                <Input
                  id="sku-code"
                  placeholder="e.g., ZR-ETH-001"
                  value={skuCode}
                  onChange={(e) => setSkuCode(e.target.value.toUpperCase())}
                  data-testid="sku-code-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="product-name">Product Name *</Label>
                <Input
                  id="product-name"
                  placeholder="e.g., Blue Silk Saree"
                  value={skuName}
                  onChange={(e) => setSkuName(e.target.value)}
                  data-testid="product-name-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="category">Category *</Label>
              <select
                id="category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                data-testid="category-select"
              >
                {categories.length > 0 ? (
                  categories.map(cat => (
                    <option key={cat.slug} value={cat.slug}>{cat.name}</option>
                  ))
                ) : (
                  <>
                    <option value="fashion">Fashion & Apparel</option>
                    <option value="jewellery">Jewellery</option>
                    <option value="cosmetics">Cosmetics & Personal Care</option>
                    <option value="kids">Kids Products</option>
                    <option value="accessories">Accessories & Footwear</option>
                  </>
                )}
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Image Upload Card */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-lg">Product Images</CardTitle>
                <CardDescription>
                  Upload multiple views for better AI generation quality
                </CardDescription>
              </div>
              <Badge variant={uploadedCount > 0 ? "default" : "outline"}>
                {uploadedCount}/4 Images
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            {/* Info Banner */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex gap-3">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">Multi-image upload improves AI quality</p>
                <p className="text-blue-600">
                  Front view is required. Back, side, and zoom views help AI generate 
                  more accurate and consistent outputs across all angles.
                </p>
              </div>
            </div>

            {/* Image Upload Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {IMAGE_TYPES.map(({ id, label, required, description, icon }) => (
                <div key={id} className="relative" data-testid={`upload-${id}`}>
                  <div
                    className={`
                      border-2 border-dashed rounded-lg p-4 transition-colors
                      ${images[id].url ? 'border-green-300 bg-green-50' : 'border-gray-300 hover:border-indigo-400'}
                      ${images[id].error ? 'border-red-300 bg-red-50' : ''}
                    `}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop(id)}
                  >
                    {/* Label */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{icon}</span>
                        <span className="font-medium text-sm">{label}</span>
                        {required && <Badge variant="outline" className="text-xs">Required</Badge>}
                      </div>
                      {images[id].url && (
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      )}
                    </div>
                    
                    {/* Upload Area or Preview */}
                    {images[id].url || images[id].previewUrl ? (
                      <div className="relative">
                        <img
                          src={images[id].previewUrl || images[id].url}
                          alt={label}
                          className="w-full h-32 object-contain rounded bg-white"
                        />
                        {images[id].uploading && (
                          <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded">
                            <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                          </div>
                        )}
                        <button
                          onClick={() => handleRemoveImage(id)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                          disabled={images[id].uploading}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center h-32 cursor-pointer">
                        <Upload className="w-8 h-8 text-gray-400 mb-2" />
                        <span className="text-sm text-gray-500">
                          Drag & drop or <span className="text-indigo-600">browse</span>
                        </span>
                        <span className="text-xs text-gray-400 mt-1">{description}</span>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleImageSelect(id, e.target.files[0])}
                        />
                      </label>
                    )}
                    
                    {/* Error */}
                    {images[id].error && (
                      <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {images[id].error}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="flex justify-end gap-4">
          <Button 
            variant="outline"
            onClick={() => navigate("/products")}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit() || saving}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="save-product-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                Save Product
              </>
            )}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AddProduct;
